import { StatusPill } from '../../../components/StatusPill'
import type { ColumnDef } from '../../../design-system/list-view/types'
import type { ProgramView } from '../../../types/cartridgeStudio'

/**
 * IB-210 Slice 2 (demo v66) — Programs grid columns (DoR §3 AC1's ratified canon set: Jurisdiction
 * · Level · Program code · Domain · Created). `Program` is platform canon (no `tenant_id`, ADR-0017)
 * — CLAUDE.md's "every cross-case list carries Account · Legal entity · Case · Fiscal year" rule
 * governs lists of tenant business data, not canon program registry rows (design doc §3's own
 * ratified note); applying that column set here would be a category error, not a design-system
 * requirement.
 *
 * `Domain` (`kind`) is filterable but NOT sortable — confirmed by direct read of
 * `CartridgeService.PROGRAM_RSQL`'s sortable-fields allow-list (`jurisdiction, level, code,
 * createdAt, id` — `kind` is filterable-only). The design doc's own column table marked Domain
 * "Yes" sortable; this is a small, disclosed correction against the real backend allow-list, not a
 * design gap silently worked around — an attempted `sort=kind` would be rejected server-side.
 */
export function buildProgramColumns(): ColumnDef<ProgramView>[] {
  return [
    {
      id: 'jurisdiction',
      header: 'Jurisdiction',
      width: 180,
      minWidth: 140,
      sortable: true,
      pinnable: true,
      toExportText: (row) => row.jurisdiction,
      render: (row) => row.jurisdiction,
    },
    {
      id: 'level',
      header: 'Level',
      width: 120,
      sortable: true,
      toExportText: (row) => row.level,
      render: (row) => row.level,
    },
    {
      id: 'code',
      header: 'Program code',
      width: 160,
      sortable: true,
      toExportText: (row) => row.code,
      render: (row) => <code style={{ fontFamily: 'var(--iw-font-mono)' }}>{row.code}</code>,
    },
    {
      id: 'kind',
      header: 'Domain',
      width: 130,
      // Not sortable — see this file's header note (real RSQL allow-list, not a design gap).
      sortable: false,
      toExportText: (row) => row.kind,
      render: (row) =>
        row.kind === 'film' ? (
          <StatusPill variant="neutral" label="Film & Media" icon="film" />
        ) : (
          <StatusPill variant="neutral" label="R&D" icon="activity" />
        ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      width: 170,
      sortable: true,
      toExportText: (row) => row.createdAt,
      render: (row) => (
        <span title={new Date(row.createdAt).toLocaleString()}>{new Date(row.createdAt).toLocaleDateString()}</span>
      ),
    },
  ]
}

export const PROGRAM_COLUMN_ORDER = ['jurisdiction', 'level', 'code', 'kind', 'createdAt']
